import { ref } from 'vue'
import { usePipelineStore } from '@/Modules/ETL/stores/pipelines'
import { withNotification } from './useNotification'

export function usePipeline() {
  const pipelineStore = usePipelineStore()
  const loading = ref(false)
  const error = ref(null)
  const pipelines = ref([])
  const selectedPipeline = ref(null)

  /**
   * Fetch all pipelines
   */
  const fetchPipelines = withNotification(
    async () => {
      loading.value = true
      error.value = null
      try {
        const response = await pipelineStore.fetchPipelines()
        pipelines.value = response || []
        return response
      } finally {
        loading.value = false
      }
    },
    {
      errorTitle: 'Failed to Load Pipelines',
      fallbackErrorMessage: 'Could not fetch pipelines. Please try again.',
    }
  )

  /**
   * Get single pipeline by ID
   */
  const fetchPipeline = async (id) => {
    loading.value = true
    error.value = null
    try {
      const pipeline = await pipelineStore.getPipeline(id)
      selectedPipeline.value = pipeline
      return pipeline
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Create new pipeline
   */
  const createPipeline = withNotification(
    async (pipelineData) => {
      loading.value = true
      try {
        const newPipeline = await pipelineStore.createPipeline(pipelineData)
        pipelines.value.push(newPipeline)
        return newPipeline
      } finally {
        loading.value = false
      }
    },
    {
      successTitle: 'Pipeline Created',
      successMessage: 'Your pipeline has been created successfully.',
      errorTitle: 'Creation Failed',
      fallbackErrorMessage: 'Failed to create pipeline. Please try again.',
    }
  )

  /**
   * Update pipeline configuration
   */
  const updatePipeline = withNotification(
    async (id, updates) => {
      loading.value = true
      try {
        const updated = await pipelineStore.updatePipeline(id, updates)

        // Update in local state
        const index = pipelines.value.findIndex((p) => p.id === id)
        if (index !== -1) {
          pipelines.value[index] = updated
        }

        if (selectedPipeline.value?.id === id) {
          selectedPipeline.value = updated
        }

        return updated
      } finally {
        loading.value = false
      }
    },
    {
      successTitle: 'Pipeline Updated',
      successMessage: 'Pipeline configuration has been saved.',
      errorTitle: 'Update Failed',
      fallbackErrorMessage: 'Failed to update pipeline. Please try again.',
    }
  )

  /**
   * Delete a pipeline
   */
  const deletePipeline = withNotification(
    async (id) => {
      loading.value = true
      try {
        await pipelineStore.deletePipeline(id)
        pipelines.value = pipelines.value.filter((p) => p.id !== id)

        if (selectedPipeline.value?.id === id) {
          selectedPipeline.value = null
        }
      } finally {
        loading.value = false
      }
    },
    {
      successTitle: 'Pipeline Deleted',
      successMessage: 'The pipeline has been removed.',
      errorTitle: 'Deletion Failed',
      fallbackErrorMessage: 'Failed to delete pipeline. Please try again.',
    }
  )

  /**
   * Execute a pipeline
   */
  const executePipeline = withNotification(
    async (id) => {
      loading.value = true
      try {
        const execution = await pipelineStore.executePipeline(id)
        return execution
      } finally {
        loading.value = false
      }
    },
    {
      successTitle: 'Pipeline Started',
      successMessage: 'Pipeline execution has been initiated.',
      errorTitle: 'Execution Failed',
      fallbackErrorMessage: 'Failed to start pipeline execution.',
    }
  )

  /**
   * Get sample data from endpoint
   */
  const getSampleData = async (connectionId, endpoint) => {
    loading.value = true
    error.value = null
    try {
      return await pipelineStore.getSampleData(connectionId, endpoint)
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get pipeline executions history
   */
  const getExecutions = async (pipelineId) => {
    loading.value = true
    error.value = null
    try {
      return await pipelineStore.getPipelineExecutions(pipelineId)
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    pipelines,
    selectedPipeline,
    fetchPipelines,
    fetchPipeline,
    createPipeline,
    updatePipeline,
    deletePipeline,
    executePipeline,
    getSampleData,
    getExecutions,
  }
}
